/**
 * An IELTS sitting (D-105) in focus mode, driven by the server state like the TCF player: the
 * placement (adaptive, one exercise at a time), Listening (4 parts, audio played once), Reading
 * (free navigation, 60 min), Writing (2 tasks) and Speaking (3 parts with the live examiner).
 */

import { ApiError, academyAttemptsAnswerCreate, useAcademyAttemptsRetrieve } from "@agencyos/api-client";
import { type IeltsState, type TefState, parseFeedback, parseTefState } from "@agencyos/core";
import { Button, Skeleton } from "@agencyos/ui-web";
import { Compass } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { ExamFrame, ExamNav, formatSeconds, sequenceCells, useClockTotal } from "../ExamFrame";
import { useCountdown } from "../shared";
import { isSkill } from "../types";
import { SkillIcon } from "../ui";
import { TefListening } from "../tef/TefListening";
import { TefSpeaking } from "../tef/TefSpeaking";
import { TefWriting } from "../tef/TefWriting";
import { ContentLangProvider } from "./contentLang";
import { ExerciseCard } from "./Exercise";
import { ListeningSection } from "./IeltsListening";
import { ReadingSection } from "./IeltsReading";
import { IeltsSpeakingSection } from "./IeltsSpeaking";
import { IeltsWritingSection } from "./IeltsWriting";
import { useAcademyPaths } from "../paths";

function useNoBackNavigation(onBack: () => void): void {
  const callback = useRef(onBack);
  useEffect(() => {
    callback.current = onBack;
  }, [onBack]);
  useEffect(() => {
    window.history.pushState({ academyGuard: true }, "");
    const onPop = () => {
      window.history.pushState({ academyGuard: true }, "");
      callback.current();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
}

/** The placement: one exercise at a time, the level moves up or down after each block. */
function PlacementSection({ attemptId, state, onChanged }: { attemptId: string; state: IeltsState; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const item = state.question;
  const training = state.mode === "training";
  if (!item) return <Skeleton style={{ height: 240 }} />;
  const check = async (response: unknown) => {
    const answer = await academyAttemptsAnswerCreate(attemptId, { question: item.id, response });
    const feedback = parseFeedback(answer.data);
    if (!training || feedback.correct === null) {
      await onChanged();
      return null;
    }
    return feedback;
  };
  return (
    <section className="ac-lesson__practice" aria-label={t("ielts.placement.title")}>
      <ExamNav cells={sequenceCells(state.total || 25, [], state.answered + 1)} />
      <div className="ac-lesson__bar">
        <p className="ac-question__count ao-num">{t("ielts.placement.count", { n: state.answered + 1, total: state.total || 25 })}</p>
        <div className="ac-progress" aria-hidden="true">
          <span style={{ transform: `scaleX(${state.total ? state.answered / state.total : 0})` }} />
        </div>
      </div>
      <p className="ac-footnote">{t("ielts.placement.adaptive")}</p>
      <div className="ac-question__sheet">
        <ExerciseCard key={item.id} item={item} onCheck={check} silent={!training} checkLabel={t("academy.player.validate")} />
      </div>
      {training && (
        <div className="ac-question__actions">
          <Button variant="secondary" onClick={() => void onChanged()}>
            {t("academy.player.next")}
          </Button>
        </div>
      )}
    </section>
  );
}

/** The player of the exams made of documents: the IELTS, and the TEF Canada (`exam`, D-109). */
export function IeltsPlayer({ id, kind, mode, exam = "ielts_general" }: { id: string; kind: string; mode: string; exam?: string }) {
  const tef = exam === "tef_canada";
  const { t } = useTranslation();
  const paths = useAcademyPaths();
  const query = useAcademyAttemptsRetrieve(id, { query: { retry: 1, staleTime: 0, refetchOnWindowFocus: false } });
  const [backWarning, setBackWarning] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  // A TEF state is an IELTS state plus its document, flow and sections A / B (parseTefState).
  const state: TefState | null = useMemo(() => (query.data?.status === 200 ? parseTefState(query.data.data) : null), [query.data]);
  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query]);
  const left = useCountdown(state?.remaining ?? 0, state ? query.dataUpdatedAt : 0, () => void refresh());
  useNoBackNavigation(() => setBackWarning(true));
  const kindName = state?.kind || kind;
  const clockTotal = useClockTotal(`${id}.${state?.section ?? ""}`, state?.remaining ?? 0, formatSeconds(query.data?.data, kindName));

  const section = state?.section ?? "";
  useEffect(() => {
    if (section) heading.current?.focus({ preventScroll: true });
  }, [section]);

  if (query.isLoading) {
    return (
      <div className="ac-player ac-player--loading">
        <Skeleton style={{ height: 64 }} />
        <Skeleton style={{ height: 360 }} />
      </div>
    );
  }
  if (query.error instanceof ApiError && query.error.status === 404) return <Navigate to={paths.home} replace />;
  if (!state) {
    return (
      <div className="ac-surface ac-card ac-card--center">
        <p className="ko">{t("academy.player.networkError")}</p>
        <Button onClick={() => void refresh()}>{t("common.retry")}</Button>
      </div>
    );
  }
  if (state.status !== "in_progress") return <Navigate to={paths.results(id)} replace />;

  const training = (state.mode || mode) === "training";
  const skills = state.skills.filter(isSkill);
  const sectionIndex = isSkill(state.section) ? skills.indexOf(state.section) : 0;
  const title = state.section === "pl" ? t("ielts.placement.title") : state.section ? t(`${tef ? "tef" : "ielts"}.skill.${state.section}`) : "";

  return (
    <ExamFrame
      className={`ac-player--ielts ac-player--${state.section || "co"}`}
      icon={
        isSkill(state.section) ? (
          <SkillIcon skill={state.section} size={18} />
        ) : (
          <span className="ac-pastille ac-tone--info" aria-hidden="true">
            <Compass size={18} />
          </span>
        )
      }
      eyebrow={`${t(`ielts.exams.${exam}.short`)}${kindName ? ` · ${t(`academy.kind.${kindName}`)}` : ""}${
        skills.length > 1 ? ` · ${t("academy.player.sectionOf", { n: sectionIndex + 1, total: skills.length })}` : ""
      }`}
      title={title}
      headingRef={heading}
      training={training}
      seconds={left}
      clockTotal={clockTotal}
      steps={skills}
      stepIndex={sectionIndex}
      quitNote={state.section === "co" ? t("examFrame.noteListening") : state.section === "eo" ? t("examFrame.noteRecording") : undefined}
      footnote={t("academy.player.saved")}
    >
      {backWarning && (
        <p className="ac-note ac-note--warning" role="alert">
          {t("academy.player.noBack")}{" "}
          <button type="button" className="ac-text-button" onClick={() => setBackWarning(false)}>
            {t("common.close")}
          </button>
        </p>
      )}
      <ContentLangProvider lang={tef ? "fr" : exam === "goethe" ? "de" : "en"}>
        {state.section === "pl" && <PlacementSection attemptId={id} state={state} onChanged={refresh} />}
        {state.section === "co" &&
          (tef ? (
            <TefListening key={`co-${id}-${state.part}`} attemptId={id} state={state} training={training} onChanged={refresh} />
          ) : (
            <ListeningSection key={`co-${id}-${state.part}`} attemptId={id} state={state} training={training} onChanged={refresh} />
          ))}
        {state.section === "ce" && <ReadingSection key={`ce-${id}`} attemptId={id} state={state} onChanged={refresh} />}
        {state.section === "ee" &&
          (tef ? (
            <TefWriting key={`ee-${id}`} attemptId={id} state={state} onChanged={refresh} />
          ) : (
            <IeltsWritingSection key={`ee-${id}`} attemptId={id} state={state} onChanged={refresh} />
          ))}
        {state.section === "eo" &&
          (tef ? (
            <TefSpeaking key={`eo-${id}`} attemptId={id} state={state} onChanged={refresh} />
          ) : (
            <IeltsSpeakingSection key={`eo-${id}`} attemptId={id} state={state} onChanged={refresh} />
          ))}
      </ContentLangProvider>
    </ExamFrame>
  );
}
